"use client";

import { type ReactNode, useEffect, useRef } from "react";

type ModalProps = {
  open: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  children: ReactNode;
};

export function Modal({ open, onClose, title, subtitle, children }: ModalProps) {
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCloseRef.current();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center tablet:items-center"
      style={{ position: "fixed", zIndex: 70 }}
      role="dialog"
      aria-modal="true"
      aria-label={title || "대화상자"}
    >
      <button
        type="button"
        aria-label="닫기"
        onClick={onClose}
        className="absolute inset-0 bg-[rgba(11,16,32,0.45)] backdrop-blur-[2px]"
      />

      <div className="trial-glass-strong trial-fade-in relative flex max-h-[92dvh] w-full flex-col rounded-t-[28px] tablet:max-w-lg tablet:rounded-[28px]">
        <div className="flex items-start justify-between gap-3 px-5 pb-2 pt-5">
          <div className="min-w-0">
            {title ? <div className="text-[16px] font-bold leading-tight tracking-tight text-[var(--trial-foreground)]">{title}</div> : null}
            {subtitle ? <div className="mt-1 text-[13px] leading-snug text-[var(--trial-muted)]">{subtitle}</div> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="닫기"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[var(--trial-muted)] transition-colors hover:bg-white/50 hover:text-[var(--trial-foreground)]"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="overflow-y-auto px-5 pb-5 pt-1">{children}</div>
      </div>
    </div>
  );
}
